import { PredictedPaiParams, PublishedParams, PredictedIktosParams, ProjectSynthesisPartner } from '@Pages/RetroSynthesis/constants';
import { getPredictedProcessingTimeLabelByValue } from '@Pages/RetroSynthesis/utils';
import { isRPSChecked } from '@Pages/RetroSynthesis/ProjectParametersDialog/RPS/mapRCSToRPS';
import { updatePredictedParamsFromBE } from '../../predictiveUtils';
import { RCS10D, RCSANY, RPS_LIBRARIES } from '../../ProjectParametersDialog/constants';

function paiParamsToString(paramName, value, params) {
  switch (paramName) {
    case PredictedPaiParams.FULL_SYNTHESIS_PLAN:
      return value ? `<b>${params[PredictedPaiParams.NUMBER_OF_PLANS]}</b> full routes (up to)` : 'Last step only';
    case PredictedPaiParams.IDENTICAL_REACTIONS_STEPS:
      return `<b>${value}</b> identical reaction steps per project (up to)`;
    case PredictedPaiParams.IDENTICAL_BUILDING_BLOCKS:
      return `<b>${value}</b> identical building blocks per project (up to)`;
    case PredictedPaiParams.PROCESSING_TIME:
      return `<b>${getPredictedProcessingTimeLabelByValue(value)}</b> processing time`;
    case PredictedPaiParams.USED_BUILDING_BLOCKS:
      return `<b>${value && value.join(' ')}</b> building blocks`;
    case PredictedPaiParams.INCLUDE_RPS:
      return `Published routes integration: ${value ? '<b>Supported</b>' : '<b>Ignored</b>'}`;
    default:
      return undefined;
  }
}

function iktosShippingTimeToString(value, params) {
  if (params?.isRCSUnchecked) return null;
  if (value.indexOf(RCS10D) >= 0) return 'RCS: delivery time <b>up to 10 days</b>';
  return 'RCS: <b>any</b> delivery time';
}

function iktosPricePerGramToString(value, params) {
  if (params?.isRCSUnchecked) return null;
  return value ? `RCS: <b>${value} ($/g)` : `RCS: <b>no</b> price limit`;
}

function iktosParamsToString(paramName, value, params) {
  switch (paramName) {
    case PredictedIktosParams.MAX_NO_OF_STEPS:
      return value === 1 ? 'First disconnect only' : `<b>${value}</b> steps per route (up to)`;
    case PredictedIktosParams.SELECTIVITY:
      return value ? 'Regioselective <b>reactions only</b>' : 'Regioselectivity <b>ignored</b>';
    case PredictedIktosParams.STEREO:
      return value ? 'Stereochemistry <b>supported</b>' : 'Stereochemistry <b>ignored</b>';
    case PredictedIktosParams.SHIPPING_TIME:
      return iktosShippingTimeToString(value, params);
    case PredictedIktosParams.MAX_PRICE_PER_GRAM:
      return iktosPricePerGramToString(value, params);
    case PredictedIktosParams.USED_BUILDING_BLOCKS:
      return `<b>${value && value.join(' ')}</b> building blocks`;
    case PredictedPaiParams.INCLUDE_RPS:
      return `Published routes integration: ${value ? '<b>Supported</b>' : '<b>Ignored</b>'}`;
    case PredictedIktosParams.PROCESSING_TIME:
      return `<b>${getPredictedProcessingTimeLabelByValue(value)}</b> processing time`;
    case PredictedIktosParams.INCLUDE_SUBSTRUCTURES:
      return value ? `<b>${value}</b> intermediates included` : '<b>No</b> intermediates defined';
    case PredictedIktosParams.EXCLUDE_SUBSTRUCTURES:
      return `<b>${value}</b> intermediates excluded`;
    default:
      return undefined;
  }
}

function publishedParamsToString(paramName, value, params) {
  switch (paramName) {
    case PublishedParams.FULL_SYNTHESIS_PLAN:
      return value ? `<b>${params[PublishedParams.NUMBER_OF_PLANS]}</b> full routes (up to)` : 'Last step only';
    case PublishedParams.MAX_BRANCH:
      return `<b>${value}</b> branches per step (up to)`;
    case PublishedParams.MAX_DEPTH:
      return `<b>${value}</b> steps per route (up to)`;
    case PublishedParams.STOP_COMMAND:
      return `<b>${value ? 'Stop' : "Don't Stop"}</b> at commercial building blocks`;
    case PublishedParams.DEFAULT_YIELD:
      return `<b>${value}%</b> yield per step (assumed, if not published)`;
    default:
      return undefined;
  }
}

export const paramsToString = (paramName, value, isPredicted, params) => {
  if (!isPredicted) return publishedParamsToString(paramName, value, params);
  if (params?.partner === ProjectSynthesisPartner.PAI) return paiParamsToString(paramName, value, params);
  if (params?.partner === ProjectSynthesisPartner.IKTOS) return iktosParamsToString(paramName, value, params);
  return undefined;
};

function mapSubstructureList(list, anyIntermediates) {
  if (list) return list.map((s) => ({ value: s }));
  if (anyIntermediates) return [];
  return null;
}

export function buildIktosParameters(predictedParametersUpdated) {
  const { usedBuildingBlocks, includeSubstructures, excludeSubstructures } = predictedParametersUpdated;
  const anyIntermediates = includeSubstructures || excludeSubstructures;

  let filteredBuildingBlocks = usedBuildingBlocks ? usedBuildingBlocks.filter((block) => [RCS10D, RCSANY].indexOf(block) < 0) : null;
  filteredBuildingBlocks = filteredBuildingBlocks ? filteredBuildingBlocks.filter((block) => !RPS_LIBRARIES.includes(block)) : null;

  return {
    ...updatePredictedParamsFromBE(predictedParametersUpdated),
    includeSubstructures: mapSubstructureList(includeSubstructures, anyIntermediates),
    excludeSubstructures: mapSubstructureList(excludeSubstructures, anyIntermediates),
    shippingTime: usedBuildingBlocks ? usedBuildingBlocks.filter((block) => [RCS10D, RCSANY].indexOf(block) >= 0) : null,
    usedBuildingBlocks: filteredBuildingBlocks && filteredBuildingBlocks.length ? filteredBuildingBlocks : null,
    [PredictedIktosParams.INCLUDE_RPS]: isRPSChecked(usedBuildingBlocks),
  };
}

export function buildPaiParameters(predictedParametersUpdated) {
  const { usedBuildingBlocks } = predictedParametersUpdated;
  const filteredBuildingBlocks = usedBuildingBlocks ? usedBuildingBlocks.filter((block) => !RPS_LIBRARIES.includes(block)) : null;
  return {
    ...predictedParametersUpdated,
    usedBuildingBlocks: filteredBuildingBlocks && filteredBuildingBlocks.length ? filteredBuildingBlocks : null,
    [PredictedPaiParams.INCLUDE_RPS]: isRPSChecked(usedBuildingBlocks),
  };
}
